//const isDev = process.env.NODE_ENV === 'development';

//import { eQuickAccess } from "types/filters/search-listings-filters";
import { ISearchListingsFilters } from "types/filters/search-listings-filters";
import type { ISearchListingsToolInput } from "types/inputs/search-listings-input";

export const PLACEHOLDER_IMAGE = "https://content.harstatic.com/media/icons/artwork/no_photo_placeholder__large.svg";

export const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
export const apiOldBaseUrl = process.env.REACT_APP_OLD_API_BASE_URL ?? "";

//export const baseUrl = process.env.PUBLIC_URL ?? (isDev? '/': '/frontend');
export const baseUrl = process.env.PUBLIC_URL ?? "";

export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
export const IP_INFO_TOKEN = process.env.REACT_APP_IP_INFO_API_TOKEN;
export const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
export const GUEST_USER_ALLOWED = (process.env.REACT_APP_GUEST_USER_ALLOWED ?? "1") === "1";

export const AGENT_FALLBACK_URL = "#";

export const ALLOWED_ORIGINS = [
    "http://localhost:3000",
    "http://aisearch-local.har.com:3100",
    "http://aisearch-local.har.com:5173",
    "http://labs-local.har.com:3100",
    "http://aisearch.har.com",
    "https://www.har.com",
    "https://www-test.har.com",
    "https://www-v2-test.har.com",
    "http://har.har.com:8080",
] as const;

export const allowedOrigins = new Set(ALLOWED_ORIGINS);

export enum eApplicationSource {
    ADMIN = "admin",
    SHARE = "share",
    MY_ACCOUNT = "myaccount",
    LISTING_DETAIL = "listing-detail"
}

export const propertyTypeObject: Record<string, number> = {
    "Single-Family": 1,
    "Townhouse/Condo": 2,
    "Residential-Lots": 3,
    "Multi-Family": 4,
    "Acreage": 5,
    "Mid/Hi-Rise Condo": 6,
};

// Rental property-type codes. HAR.com sends `prop_type` (not `property_class_id`)
// for for-rent searches — see share_url_util._build_params, which pairs
// `prop_type` with `for_sale=0` and `property_class_id` with `for_sale=1`.
// Mirror of the backend's map_property_types_to_prop_types
// (app/ai_search/utils/tool_utils.py), keyed on the same canonical names as
// `propertyTypeObject` above so the two tables read as a pair.
export const propTypeCodeObject: Record<string, string> = {
    "Single-Family": "SGL",
    "Townhouse/Condo": "THC",
    "Residential-Lots": "LOT",
    "Multi-Family": "MLT",
    "Acreage": "ACR",
    "Mid/Hi-Rise Condo": "HIR",
};

export type QuickAccess = 'new_entry'
    | 'Foreclosure'
    | 'pricereduced'
    | 'open_houses'
    | 'voh_only'
    | 'new_constr2';

export const categoriesMap = {
    stype: 'new_entry',
    forcl: 'Foreclosure',
    new_constr2: 'new_constr2',
    pricereduced: 'pricereduced',
    open_houses: 'open_houses',
    voh_only: 'voh_only',
} satisfies
    Partial<Record<keyof ISearchListingsFilters, QuickAccess>>;

/**
 * Quick-access category -> the boolean field that carries it on the search tool
 * input.
 *
 * `categoriesMap`'s VALUES are UI names (`IFilters.Category`, `QuickAccess`) and
 * are consumed as such all over the filter modal and the pills, so they cannot
 * simply be renamed. They also used to be written straight onto the tool input,
 * which happened to work for every category except `Foreclosure`: the backend
 * names that field after HAR's own flag, `forcl` (see `SearchListingsToolInput`
 * in search_listings_tool_input.py). The operation model whitelists tool-input
 * field names, so `Foreclosure` was dropped before the search ran (CB-1756).
 *
 * TOTAL on purpose, and not `Partial` + an identity fallback: the five matching
 * rows are spelled out so that adding a category whose UI name is not a field on
 * `ISearchListingsToolInput` is a compile error here, rather than a value the
 * backend silently drops at runtime.
 */
const QUICK_ACCESS_TOOL_INPUT_KEY: Record<QuickAccess, keyof ISearchListingsToolInput> = {
    new_entry: "new_entry",
    Foreclosure: "forcl",
    pricereduced: "pricereduced",
    open_houses: "open_houses",
    voh_only: "voh_only",
    new_constr2: "new_constr2",
};

/** The tool-input field name for a quick-access category. */
export function toolInputKeyForQuickAccess(category: QuickAccess): keyof ISearchListingsToolInput {
    return QUICK_ACCESS_TOOL_INPUT_KEY[category];
}


export const socialLinksForSignIn = [
    {
        name: "Google",
        icon: "https://content.harstatic.com/media/icons/social_colored/googleg_colored.svg",
        classname: "sc_google",
        connection_id: "google-oauth2",
    },
    {
        name: "Apple",
        icon: "https://content.harstatic.com/media/icons/social/social_icon_apple.svg",
        classname: "sc_apple",
        connection_id: "apple",
    },
    {
        name: "Microsoft Accounts",
        icon: "https://content.harstatic.com/media/icons/map_popup/windows-copy-3.svg",
        classname: "sc_windows",
        connection_id: "windowslive",
    },
    {
        name: "Amazon",
        icon: "https://content.harstatic.com/media/icons/social/social_icon_amazon.svg",
        classname: "sc_amazon",
        connection_id: "amazon",
    },
    {
        name: "LinkedIn",
        icon: "https://content.harstatic.com/media/icons/social_colored/linkedin_colored.svg",
        classname: "sc_linkedin",
        connection_id: "linkedin",
    },
    {
        name: "Twitter",
        icon: "https://content.harstatic.com/media/icons/social/icons-social-network-twitter-color.svg",
        classname: "sc_twitter",
        connection_id: "twitter",
    },
];
